import type { Candle } from './api';
import type { DisplayTrade } from '../components/Trades';

const NANOSECONDS = 1_000_000_000n;
export interface ChartCandle { time: number; open: number; high: number; low: number; close: number; volume: number; rising: boolean }
export interface TradeSummary { count: number; tokens: bigint; knownUsd: number; unknownUsdCount: number }
export interface WalletTradeGroup extends TradeSummary { id: string; time: number; buy: boolean; trades: DisplayTrade[] }
export interface WalletChartTrades { groups: WalletTradeGroup[]; buy: TradeSummary; sell: TradeSummary; matchedTradeCount: number; excludedTradeCount: number }
const emptySummary = (): TradeSummary => ({ count: 0, tokens: 0n, knownUsd: 0, unknownUsdCount: 0 });
const knownUsd = (value: number | null): value is number => value !== null && Number.isFinite(value) && value >= 0;
function add(summary: TradeSummary, trade: DisplayTrade) {
  summary.count++;
  summary.tokens += trade.tokens;
  if (knownUsd(trade.usd) && Number.isFinite(summary.knownUsd + trade.usd)) summary.knownUsd += trade.usd;
  else summary.unknownUsdCount++;
}

/** Chart timestamps must be unique seconds; reject invalid prices before scaling. */
export function prepareChartCandles(candles: Candle[], multiplier = 1, volumeMultiplier = 1): ChartCandle[] {
  if (!Number.isFinite(multiplier) || multiplier <= 0 || !Number.isFinite(volumeMultiplier) || volumeMultiplier < 0) return [];
  const byTime = new Map<number, ChartCandle>();
  for (const candle of candles) {
    if (candle.t < 0n || candle.volume < 0n) continue;
    const time = Number(candle.t / NANOSECONDS);
    const prices = [candle.open, candle.high, candle.low, candle.close];
    if (!Number.isSafeInteger(time) || time > 8_640_000_000_000 || !prices.every(value => Number.isFinite(value) && value >= 0 && Number.isFinite(value * multiplier))) continue;
    if (candle.high < Math.max(candle.open, candle.close) || candle.low > Math.min(candle.open, candle.close) || candle.low > candle.high) continue;
    const volume = Number(candle.volume) * volumeMultiplier;
    if (!Number.isFinite(volume)) continue;
    byTime.set(time, { time, open: candle.open * multiplier, high: candle.high * multiplier, low: candle.low * multiplier, close: candle.close * multiplier, volume, rising: candle.close >= candle.open });
  }
  return [...byTime.values()].sort((a, b) => a.time - b.time);
}

/** Match exact UTC candle starts, never let the chart snap a trade across a gap. */
export function groupWalletChartTrades(candles: Pick<ChartCandle, 'time'>[], trades: DisplayTrade[], wallet: string, intervalSeconds: 60 | 3600): WalletChartTrades {
  const result: WalletChartTrades = { groups: [], buy: emptySummary(), sell: emptySummary(), matchedTradeCount: 0, excludedTradeCount: 0 };
  if (!wallet) return result;
  const candleTimes = new Set(candles.map(candle => candle.time));
  const buckets = new Map<string, WalletTradeGroup>();
  const seen = new Set<string>();
  const intervalNs = BigInt(intervalSeconds) * NANOSECONDS;
  for (const trade of trades) {
    if (trade.who !== wallet) continue;
    const tradeKey = `${trade.market}:${trade.id}`;
    if (seen.has(tradeKey)) continue;
    seen.add(tradeKey);
    const time = Number((trade.at / intervalNs) * BigInt(intervalSeconds));
    if (trade.at < 0n || trade.at / 1_000_000n > 8_640_000_000_000_000n || trade.tokens < 0n || !Number.isSafeInteger(time) || !candleTimes.has(time)) { result.excludedTradeCount++; continue; }
    const id = `${time}:${trade.buy ? 'buy' : 'sell'}`;
    let bucket = buckets.get(id);
    if (!bucket) { bucket = { id, time, buy: trade.buy, trades: [], ...emptySummary() }; buckets.set(id, bucket); }
    bucket.trades.push(trade);
    add(bucket, trade);
    add(trade.buy ? result.buy : result.sell, trade);
    result.matchedTradeCount++;
  }
  result.groups = [...buckets.values()].sort((a, b) => a.time - b.time || Number(b.buy) - Number(a.buy));
  for (const group of result.groups) group.trades.sort((a, b) => a.at < b.at ? -1 : a.at > b.at ? 1 : a.id.localeCompare(b.id));
  return result;
}
